import { TZDate } from '@date-fns/tz'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { format } from 'date-fns/format'
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow'
import { isThisYear } from 'date-fns/isThisYear'
import { isToday } from 'date-fns/isToday'
import { isTomorrow } from 'date-fns/isTomorrow'
import { isValid } from 'date-fns/isValid'
import { parse } from 'date-fns/parse'
import { parseISO } from 'date-fns/parseISO'

import { makeOption } from '@/components/flows/CompassFlow/helpers'
import { DATE_FORMATS } from '@/lib/constants'
import { SelectOption } from '@/types/misc'

/**
 * @see https://date-fns.org/docs/format for date formats.
 */
export function formatDate(
  date: Date | string,
  dateFormat = DATE_FORMATS.DAY_MONTH_YEAR,
  options?: { timeZone?: string | null },
): string {
  let dateObj = date instanceof Date ? date : parseISO(date)
  const { timeZone } = options || {}

  if (timeZone) {
    dateObj = new TZDate(dateObj.toISOString(), timeZone)
  }

  return format(dateObj, dateFormat)
}

/**
 * Date and time in the viewer's locale and timezone, e.g. "5 Oct 2026, 14:00"
 * for en-GB.
 */
export function formatLocaleDateTime(date: Date | string): string {
  const dateObj = date instanceof Date ? date : parseISO(date)

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj)
}

/**
 * Date and time range in the viewer's locale and timezone, e.g.
 * "Monday 5 October 2026, 14:00-14:30" for en-GB.
 */
export function formatLocaleDateTimeRange(
  start: Date | string,
  end: Date | string,
): string {
  const startObj = start instanceof Date ? start : parseISO(start)
  const endObj = end instanceof Date ? end : parseISO(end)

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'full',
    timeStyle: 'short',
  }).formatRange(startObj, endObj)
}

/**
 * Natural-language label for a date relative to today, e.g. "Today",
 * "Tomorrow" or "This Thursday". Returns null when the date is further out than
 * the coming week, so callers can fall back to showing the full date.
 */
export function getRelativeDayLabel(date: Date | string): string | null {
  const dateObj = date instanceof Date ? date : parseISO(date)

  if (isToday(dateObj)) {
    return 'Today'
  }

  if (isTomorrow(dateObj)) {
    return 'Tomorrow'
  }

  const daysAway = differenceInCalendarDays(dateObj, new Date())

  if (daysAway > 1 && daysAway <= 6) {
    return `This ${format(dateObj, 'EEEE')}`
  }

  return null
}

/**
 * Natural-language description of how long ago a date was, e.g.
 * "5 minutes ago" or "about 2 hours ago".
 */
export function formatTimeAgo(date: Date | string): string {
  const dateObj = date instanceof Date ? date : parseISO(date)
  return formatDistanceToNow(dateObj, { addSuffix: true })
}

/**
 * Compact date such as "1 Jul" for dates in the current year, or "1 Jul 25"
 * for dates in a previous year.
 */
export function formatShortDate(date: Date | string): string {
  const dateObj = date instanceof Date ? date : parseISO(date)
  return format(dateObj, isThisYear(dateObj) ? 'd MMM' : 'd MMM yy')
}

export function parseDate(dateString: string, dateFormat: string) {
  return parse(dateString, dateFormat, new Date())
}

export function convertDate(
  dateString: string,
  fromFormat: string,
  toFormat: string,
): string {
  const parsedDate = parseDate(dateString, fromFormat)
  return formatDate(parsedDate, toFormat)
}

export function convertIsoDate(dateOfBirth: string, format: string): string {
  const parsedDate = parseISO(dateOfBirth)
  return formatDate(parsedDate, format)
}

export function isValidDateFormat(
  dateString: string,
  dateFormat: string,
): boolean {
  const parsedDate = parse(dateString, dateFormat, new Date())

  return isValid(parsedDate)
}

export function getMonthNameFromNumber(monthNumber: number): string {
  const months = [
    '',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const month = months[monthNumber]

  if (!month) {
    throw new Error('Invalid month number. Month must be between 1-12')
  }

  return month
}

export function padDatePartWithZero(datePart: string | number): string {
  return String(datePart).padStart(2, '0')
}

// Convert date of birth from DD/MM/YYYY to YYYY-MM-DD
export function normalizeDateOfBirth(dateOfBirth: string): string {
  const parsedDate = parseDate(dateOfBirth, DATE_FORMATS.DAY_MONTH_YEAR)
  return formatDate(parsedDate, DATE_FORMATS.YEAR_MONTH_DAY)
}

export function makeYearOptions(numYears: number): SelectOption[] {
  const yearOptions = new Array(numYears - 1).fill(0).map((_, index) => {
    const year = index + 1
    const yearLabel = year === 1 ? '1 year' : `${year} years`

    return makeOption(yearLabel)
  })

  return [
    { label: 'Less than a year', value: '0 years' },
    ...yearOptions,
    { label: `${numYears}+ years`, value: `${numYears}+` },
  ]
}

export function convertDateIfValid(date: string) {
  return date && isValidDateFormat(date, DATE_FORMATS.YEAR_MONTH_DAY)
    ? convertDate(
        date,
        DATE_FORMATS.YEAR_MONTH_DAY,
        DATE_FORMATS.DAY_MONTH_YEAR,
      )
    : ''
}
